import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { INDICATOR_COLORS } from './utils';

export default function CorrelationSection({ backtest }) {
  const correlationData = backtest.correlation_data || null;
  if (!correlationData || !correlationData.matrix) return null;

  // Build merged rolling-correlation data: one object per trading day so recharts
  // renders every bar (per-Line data props cause category-axis collapsing).
  const rollLabels = correlationData.rolling ? Object.keys(correlationData.rolling) : [];
  const rollData = (() => {
    if (!correlationData.rolling) return [];
    const dateMap = {};
    rollLabels.forEach((label) => {
      (correlationData.rolling[label] || []).forEach(({ date, value }) => {
        if (!dateMap[date]) dateMap[date] = { date };
        dateMap[date][label] = value;
      });
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  })();

  return (
    <>
      <h3 style={{ marginTop: 28, color: 'var(--text)' }}>Correlation Analysis</h3>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
        Pearson correlation of daily returns between {backtest.symbol} and comparison assets
      </div>

      {/* Correlation Matrix Heatmap */}
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}></th>
              {correlationData.matrix.labels.map((label, i) => (
                <th key={i} style={{ padding: '6px 10px', textAlign: 'center', color: 'var(--text)', fontWeight: 600, fontSize: 11 }}>
                  {label === 'base' ? backtest.symbol : label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {correlationData.matrix.values.map((row, ri) => (
              <tr key={ri}>
                <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text)', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {correlationData.matrix.labels[ri] === 'base' ? backtest.symbol : correlationData.matrix.labels[ri]}
                </td>
                {row.map((val, ci) => {
                  const intensity = Math.abs(val);
                  const hue = val >= 0 ? '142' : '0';
                  const bg = ri === ci ? 'var(--subtle)' : `hsla(${hue}, 70%, 50%, ${intensity * 0.5})`;
                  return (
                    <td key={ci} style={{
                      padding: '6px 10px',
                      textAlign: 'center',
                      background: bg,
                      color: 'var(--text)',
                      fontWeight: intensity > 0.5 ? 700 : 400,
                      borderRadius: 2,
                    }}>
                      {val.toFixed(2)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Rolling Correlation Chart */}
      {rollData.length > 0 && (
        <>
          <h4 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 14 }}>Rolling 30-Day Correlation</h4>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={rollData} margin={{ top: 5, right: 10, bottom: 0, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'var(--muted)' }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} domain={[-1, 1]}
                tickFormatter={(v) => v.toFixed(1)} />
              <Tooltip
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
                formatter={(val, name) => [typeof val === 'number' ? val.toFixed(3) : val, name]}
              />
              <Legend />
              {/* Zero reference line */}
              <Line dataKey="_zero" stroke="var(--border)" strokeDasharray="4 4" dot={false} legendType="none"
                data={rollData.map((d) => ({ ...d, _zero: 0 }))} name="" />
              {rollLabels.map((label, i) => (
                <Line
                  key={label}
                  dataKey={label}
                  stroke={INDICATOR_COLORS[i % INDICATOR_COLORS.length]}
                  dot={false}
                  name={label}
                  strokeWidth={1.5}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </>
  );
}
